import React, { useState, useEffect } from 'react';
import { safeJson, networkMessage } from '../apiHelpers';
import { t } from '../i18n';

function Connections({ user }) {
  const [data, setData] = useState({ connections: [], pending_received: [], pending_sent: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState({});

  useEffect(() => {
    if (user) fetchConnections();
    const interval = setInterval(() => { if (user) fetchConnections(); }, 5000);
    return () => clearInterval(interval);
  }, [user]);

  const fetchConnections = async () => {
    try {
      const res = await fetch('/api/connections/my/', {
        headers: { 'Authorization': `Token ${user.token}` }
      });
      const d = await safeJson(res);
      setData(d);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleConnection = async (connId, action) => {
    setActing(prev => ({ ...prev, [connId]: true }));
    try {
      await fetch(`/api/connections/handle/${connId}/`, {
        method: 'POST',
        headers: { 'Authorization': `Token ${user.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action })
      });
      fetchConnections();
    } catch (err) { console.error(err); }
    setActing(prev => ({ ...prev, [connId]: false }));
  };

  if (!user) return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={{ textAlign: 'center', color: '#6200ea' }}>🔑 Login Required</h2>
        <p style={{ textAlign: 'center', color: '#666' }}>{t('cn_login')}</p>
      </div>
    </div>
  );

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>🤝 My Network</h1>

      {/* Pending Requests */}
      {data.pending_received.length > 0 && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>📬 Pending Requests ({data.pending_received.length})</h2>
          {data.pending_received.map(conn => (
            <div key={conn.id} style={styles.card}>
              <div style={styles.userRow}>
                <a href={`/profile/${conn.username}`} style={styles.userLink}>
                  {conn.avatar ? <img src={conn.avatar} alt={conn.username} style={styles.avatar} /> : <div style={styles.avatarPlaceholder}>{conn.username[0].toUpperCase()}</div>}
                  <span style={styles.username}>@{conn.username}</span>
                </a>
                <div style={styles.actions}>
                  <button style={styles.acceptBtn} onClick={() => handleConnection(conn.id, 'accept')} disabled={acting[conn.id]}>✅ Accept</button>
                  <button style={styles.rejectBtn} onClick={() => handleConnection(conn.id, 'reject')} disabled={acting[conn.id]}>❌ Reject</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Connections */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>👥 Connections ({data.total})</h2>
        {loading ? <p style={{ color: '#999', textAlign: 'center' }}>{t('loading')}</p> :
          data.connections.length === 0 ? (
            <div style={styles.empty}>
              <p>{t('cn_empty')}</p>
              <p style={{ fontSize: '13px', color: '#999' }}>{t('cn_empty_hint')}</p>
            </div>
          ) : (
            data.connections.map(conn => (
              <div key={conn.id} style={styles.card}>
                <div style={styles.userRow}>
                  <a href={`/profile/${conn.username}`} style={styles.userLink}>
                    {conn.avatar ? <img src={conn.avatar} alt={conn.username} style={styles.avatar} /> : <div style={styles.avatarPlaceholder}>{conn.username[0].toUpperCase()}</div>}
                    <span style={styles.username}>@{conn.username}</span>
                  </a>
                  <a href={`/profile/${conn.username}`} style={styles.viewBtn}>{t('cn_view_profile')}</a>
                </div>
              </div>
            ))
          )
        }
      </div>

      {/* Sent Requests */}
      {data.pending_sent.length > 0 && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>📤 Sent Requests ({data.pending_sent.length})</h2>
          {data.pending_sent.map(conn => (
            <div key={conn.id} style={styles.card}>
              <div style={styles.userRow}>
                <a href={`/profile/${conn.username}`} style={styles.userLink}>
                  <div style={styles.avatarPlaceholder}>{conn.username[0].toUpperCase()}</div>
                  <span style={styles.username}>@{conn.username}</span>
                </a>
                <span style={styles.pendingBadge}>⏳ Pending</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { maxWidth: '700px', margin: '20px auto', padding: '20px' },
  title: { color: '#6200ea', marginBottom: '20px' },
  section: { marginBottom: '24px' },
  sectionTitle: { color: '#333', fontSize: '18px', marginBottom: '12px', borderBottom: '2px solid #f3e5f5', paddingBottom: '8px' },
  card: { backgroundColor: 'white', borderRadius: '12px', padding: '15px', marginBottom: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  userRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' },
  userLink: { display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none', color: '#333' },
  avatar: { width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #6200ea' },
  avatarPlaceholder: { width: '44px', height: '44px', borderRadius: '50%', backgroundColor: '#6200ea', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '18px' },
  username: { fontWeight: 'bold', fontSize: '15px', color: '#6200ea' },
  actions: { display: 'flex', gap: '8px' },
  acceptBtn: { backgroundColor: '#10b981', color: 'white', border: 'none', padding: '7px 14px', borderRadius: '15px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' },
  rejectBtn: { backgroundColor: '#ff4444', color: 'white', border: 'none', padding: '7px 14px', borderRadius: '15px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' },
  viewBtn: { backgroundColor: '#f3e5f5', color: '#6200ea', padding: '7px 14px', borderRadius: '15px', textDecoration: 'none', fontWeight: 'bold', fontSize: '13px' },
  pendingBadge: { backgroundColor: '#fff3e0', color: '#0d9488', padding: '6px 12px', borderRadius: '15px', fontSize: '13px', fontWeight: 'bold' },
  empty: { textAlign: 'center', padding: '30px', backgroundColor: 'white', borderRadius: '12px', color: '#666' },
};

export default Connections;
